const LETTERS='CDEFGAB',SEMITONES=[0,2,4,5,7,9,11];
export function parseTriadRoot(root){
 const m=String(root||'').trim().replaceAll('♯','#').replaceAll('♭','b').match(/^([A-Ga-g])([#b]?)$/);
 return m?{letter:m[1].toUpperCase(),accidental:m[2]}:null;
}
export function spellMajorTriad(root){
 const parsed=parseTriadRoot(root);if(!parsed)throw Error('Choose a root letter and optional sharp or flat.');
 const index=LETTERS.indexOf(parsed.letter),rootPitch=SEMITONES[index]+(parsed.accidental==='#'?1:parsed.accidental==='b'?-1:0);
 return [0,2,4].map((step,i)=>{
  const target=(index+step)%7,octave=Math.floor((index+step)/7);
  const delta=rootPitch+[0,4,7][i]-(SEMITONES[target]+12*octave);
  if(Math.abs(delta)>2)throw Error('This root requires an unsupported accidental.');
  return LETTERS[target]+(delta>0?'♯'.repeat(delta):'♭'.repeat(-delta));
 });
}
export function syncMajorTriadSpell(config){
 if(config.variant==='triad-qualities'){
  try{config.correct=triadQualityAnswers(config);}catch{config.correct={};}
  return config;
 }
 if(config.variant==='primary-triads'){
  try{config.correct=primaryTriadAnswers(config);}catch{config.correct={};}
  return config;
 }
 if (config.variant === 'tonic-dominant') {
  config.correct = spellTonicDominant(config.key || 'e', config.scaleForm || 'harmonic');
  return config;
 }
 config.correct={};
 for(const row of config.rows||[]){
  try{const [root,third,fifth]=spellMajorTriad(row.root);row.root=root;config.correct[`${row.id}_third`]=third;config.correct[`${row.id}_fifth`]=fifth;}
  catch{config.correct[`${row.id}_third`]='';config.correct[`${row.id}_fifth`]='';}
 }
 return config;
}
export function addMajorTriadRow(config){
 config.rows ||= [];let n=1;while(config.rows.some(r=>r.id===`row${n}`))n++;
 config.rows.push({id:`row${n}`,root:'C'});return syncMajorTriadSpell(config);
}
export function removeMajorTriadRow(config,id){config.rows=(config.rows||[]).filter(r=>r.id!==id);return syncMajorTriadSpell(config);}

// Level 10 bonus: spell both diatonic triads in root position.  In minor
// keys, the harmonic (and ascending melodic) form raises the leading tone,
// making V major rather than v minor.  The requested letter case expresses
// chord quality: lowercase for minor triads, uppercase for major triads.
export const MINOR_KEY_CHOICES = [
  'a', 'e', 'b', 'f#', 'c#', 'g#', 'd#', 'a#',
  'd', 'g', 'c', 'f', 'bb', 'eb', 'ab'
];

function spellMinorTriad(root) {
  const parsed = parseTriadRoot(root);
  if (!parsed) throw Error('Choose a minor key.');
  const index = LETTERS.indexOf(parsed.letter);
  const pitch = SEMITONES[index] + (parsed.accidental === '#' ? 1 : parsed.accidental === 'b' ? -1 : 0);
  return [0, 2, 4].map((step, i) => {
    const target = (index + step) % 7;
    const octave = Math.floor((index + step) / 7);
    const delta = pitch + [0, 3, 7][i] - (SEMITONES[target] + 12 * octave);
    if (Math.abs(delta) > 2) throw Error('This key requires an unsupported accidental.');
    return LETTERS[target] + (delta > 0 ? '♯'.repeat(delta) : '♭'.repeat(-delta));
  });
}

export function spellTonicDominant(key = 'e', scaleForm = 'harmonic') {
  const tonicMinor = spellMinorTriad(key);
  // The fifth of the tonic always gives the correctly *spelled* scale degree 5.
  const fifth = tonicMinor[2];
  const dominantMajor = scaleForm !== 'natural';
  const dominant = dominantMajor ? spellMajorTriad(fifth) : spellMinorTriad(fifth);
  const useCase = (note, upper) => upper ? note : note[0].toLowerCase() + note.slice(1);
  return {
    tonic_root: useCase(tonicMinor[0], false),
    tonic_third: useCase(tonicMinor[1], false),
    tonic_fifth: useCase(tonicMinor[2], false),
    dominant_root: useCase(dominant[0], dominantMajor),
    dominant_third: useCase(dominant[1], dominantMajor),
    dominant_fifth: useCase(dominant[2], dominantMajor),
  };
}

export function tonicDominantKeyLabel(config) {
  const key = String(config.key || 'e').replaceAll('#', '♯').replaceAll('b', '♭');
  const form = config.scaleForm || 'harmonic';
  return `${key} minor, ${form} form`;
}

export const PRIMARY_TRIAD_GROUPS = [
 {id:'tonic',label:'Tonic'}, {id:'subdominant',label:'Subdominant'}, {id:'dominant',label:'Dominant'}
];
export function primaryTriadKeyLabel(row) {
 const parsed=parseTriadRoot(row.key);
 if(!parsed)throw Error('Enter a key such as A, f, or B♭.');
 const letter=row.mode==='minor'?parsed.letter.toLowerCase():parsed.letter;
 return letter+parsed.accidental.replaceAll('#','♯').replaceAll('b','♭')+(row.mode==='minor'?' minor, harmonic form':' Major');
}
export function spellPrimaryTriads(row) {
 const root=parseTriadRoot(row.key);
 if(!root)throw Error('Enter a key such as A, f, or B♭.');
 const minor=row.mode==='minor';
 const tonic=minor?spellMinorTriad(row.key):spellMajorTriad(row.key);
 const rootIndex=LETTERS.indexOf(root.letter);
 const fourthIndex=(rootIndex+3)%7;
 const rootPitch=SEMITONES[rootIndex]+(root.accidental==='#'?1:root.accidental==='b'?-1:0);
 const fourthDelta=rootPitch+5-(SEMITONES[fourthIndex]+(fourthIndex<rootIndex?12:0));
 const fourth=LETTERS[fourthIndex]+(fourthDelta>0?'#'.repeat(fourthDelta):'b'.repeat(-fourthDelta));
 const chords={tonic,subdominant:minor?spellMinorTriad(fourth):spellMajorTriad(fourth),dominant:spellMajorTriad(tonic[2])};
 const answer={};
 for(const group of PRIMARY_TRIAD_GROUPS)for(const [index,member] of ['root','third','fifth'].entries()){
  const note=chords[group.id][index];
  answer[`${group.id}_${member}`]=minor&&group.id!=='dominant'?note[0].toLowerCase()+note.slice(1):note;
 }
 return answer;
}
export function primaryTriadAnswers(config) {
 const answer={};
 for(const row of config.rows||[])for(const [id,note] of Object.entries(spellPrimaryTriads(row)))answer[`${row.id}_${id}`]=note;
 return answer;
}

export const TRIAD_QUALITY_GROUPS = [
 {id:'major',label:'Major'}, {id:'augmented',label:'Augmented'},
 {id:'minor',label:'minor'}, {id:'diminished',label:'diminished'}
];
export function triadQualityAnswers(config) {
 const answers={};
 for(const row of config.rows||[]) {
  spellMajorTriad(row.root);
  const root=parseTriadRoot(row.root);
  const rootIndex=LETTERS.indexOf(root.letter);
  const rootPitch=SEMITONES[rootIndex]+(root.accidental==='#'?1:root.accidental==='b'?-1:0);
  for(const group of TRIAD_QUALITY_GROUPS) {
   const intervals={major:[0,4,7],augmented:[0,4,8],minor:[0,3,7],diminished:[0,3,6]}[group.id];
   for(const [i,member] of ['root','third','fifth'].entries()) {
    const target=(rootIndex+i*2)%7,octave=Math.floor((rootIndex+i*2)/7);
    const delta=rootPitch+intervals[i]-(SEMITONES[target]+12*octave);
    if(Math.abs(delta)>2)throw Error('Unsupported accidental');
    const lower=group.id==='minor'||group.id==='diminished';
    answers[`${row.id}_${group.id}_${member}`]=(lower?LETTERS[target].toLowerCase():LETTERS[target])+(delta>0?'♯'.repeat(delta):'♭'.repeat(-delta));
   }
  }
 }
 return answers;
}
